import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createSession, joinSession, SessionError } from '../../lib/session'
import { getUserOrganizations } from '../../lib/organization'
import { X, LogOut, Eye, EyeOff, Users, User, ChevronDown } from 'lucide-react'
import { useToast } from '../common/Toast'
import { useAuth } from '../auth/AuthProvider'
import { signOut, signInWithGoogle } from '../../lib/auth'
import CreatorAuthGate from '../auth/CreatorAuthGate'
import CreateOrgModal from '../org/CreateOrgModal'
import DeleteAccountModal from '../account/DeleteAccountModal'
import type { Organization } from '../../types'

import HeroSection from './HeroSection'
import OldVsNewSection from './OldVsNewSection'
import HowItWorksSection from './HowItWorksSection'
import AgentShowcaseSection from './AgentShowcaseSection'
import MethodologyTimeline from './MethodologyTimeline'
import BenefitsGrid from './BenefitsGrid'
import SecurityTrustSection from './SecurityTrustSection'
import WhatsIncludedSection from './WhatsIncludedSection'
import FaqSection from './FaqSection'
import FooterCta from './FooterCta'

export default function LandingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { addToast } = useToast()
  const { user, verified } = useAuth()
  const [modal, setModal] = useState<'start' | 'join' | null>(null)
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [authComplete, setAuthComplete] = useState(false)
  const [sessionType, setSessionType] = useState<'team' | 'solo'>('team')

  // User's organizations
  const [userOrgs, setUserOrgs] = useState<Organization[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  // @ts-ignore used in teams menu
  const [showTeamsMenu, setShowTeamsMenu] = useState(false)
  const [showAccountMenu, setShowAccountMenu] = useState(false)

  // Start form
  const [sessionName, setSessionName] = useState('')
  const [startPassword, setStartPassword] = useState('')
  const [startName, setStartName] = useState('')

  // Join form
  const [roomCode, setRoomCode] = useState('')
  const [joinPassword, setJoinPassword] = useState('')
  const [joinName, setJoinName] = useState('')

  // Password visibility
  const [showStartPassword, setShowStartPassword] = useState(false)
  const [showJoinPassword, setShowJoinPassword] = useState(false)

  // Pre-fill display name from Google profile
  useEffect(() => {
    if (user?.displayName && !startName) {
      setStartName(user.displayName)
    }
  }, [user?.displayName])

  // If returning verified user opens start modal, skip auth gate
  useEffect(() => {
    if (modal === 'start' && verified && !authComplete) {
      handleAuthReady()
    }
  }, [modal, verified])

  // Load user's organizations
  useEffect(() => {
    if (!user?.uid) {
      setUserOrgs([])
      return
    }
    getUserOrganizations(user.uid).then(setUserOrgs)
  }, [user?.uid])

  // Handle org and deep-link URL params
  useEffect(() => {
    const orgParam = searchParams.get('org')
    if (orgParam) {
      setSelectedOrgId(orgParam)
      setModal('start')
    }
    const action = searchParams.get('action')
    if (action === 'start') {
      const mode = searchParams.get('mode')
      setSessionType(mode === 'solo' ? 'solo' : 'team')
      setModal('start')
      const url = new URL(window.location.href)
      url.searchParams.delete('action')
      url.searchParams.delete('mode')
      window.history.replaceState({}, '', url.toString())
    }
    const joinParam = searchParams.get('join')
    if (joinParam) {
      setRoomCode(joinParam.toUpperCase())
      setModal('join')
      const url = new URL(window.location.href)
      url.searchParams.delete('join')
      window.history.replaceState({}, '', url.toString())
    }
  }, [searchParams])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const isSolo = sessionType === 'solo'
    try {
      const result = await createSession(
        sessionName.trim(),
        isSolo ? '' : startPassword,
        startName.trim(),
        user?.uid,
        selectedOrgId || undefined,
        isSolo,
      )
      sessionStorage.setItem('participantId', result.participantId)
      sessionStorage.setItem('displayName', startName.trim())
      navigate(isSolo ? `/session/${result.sessionId}` : `/lobby/${result.sessionId}`)
    } catch (err: any) {
      console.error('Session creation failed:', err)
      // SIGN_IN_REQUIRED and the quota errors carry prose written for the reader;
      // show those verbatim rather than flattening them into the generic line.
      const message =
        err instanceof SessionError && (err.code === 'SIGN_IN_REQUIRED' || err.status === 429)
          ? err.message
          : 'Failed to create session. Please try again.'
      setError(message)
      addToast(message, 'error')
    } finally {
      setLoading(false)
    }
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await joinSession(roomCode.trim().toUpperCase(), joinPassword, joinName.trim())
      if (!result) {
        setError('Invalid room code or password.')
        addToast('Invalid room code or password.', 'error')
        return
      }
      sessionStorage.setItem('participantId', result.participantId)
      sessionStorage.setItem('displayName', joinName.trim())
      navigate(`/lobby/${result.sessionId}`)
    } catch {
      setError('Failed to join session. Please try again.')
      addToast('Failed to join session. Please try again.', 'error')
    } finally {
      setLoading(false)
    }
  }

  function handleAuthReady() {
    setAuthComplete(true)
  }

  function closeModal() {
    setModal(null)
    setError('')
    setAuthComplete(false)
  }

  // The Start button always means a team session; only the deep link asks for solo.
  function openStartModal(mode: 'team' | 'solo' = 'team') {
    setSessionType(mode)
    setModal('start')
  }

  function scrollToHow() {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-stone font-body text-navy">
      {/* Sticky Nav */}
      <nav className="sticky top-0 z-40 bg-navy text-stone px-6 py-3 flex items-center justify-between border-b-2 border-golden">
        <div className="flex items-center gap-3">
          <span className="hl text-xl text-stone">Day One</span>
          <span className="mono-label text-golden border border-golden/60 px-1.5 py-0.5">
            Beta
          </span>
        </div>
        <div className="flex items-center gap-3">
          {!user && (
            <button
              onClick={() => signInWithGoogle()}
              className="mono-label text-stone/75 hover:text-stone transition px-3 py-1.5 hover:bg-white/10 cursor-pointer"
            >
              Sign in
            </button>
          )}
          {user && (
            <button
              onClick={() => navigate('/sessions')}
              className="mono-label text-stone/75 hover:text-stone transition px-3 py-1.5 hover:bg-white/10 cursor-pointer"
            >
              My sessions
            </button>
          )}
          {user && (
            <div className="relative">
              <button
                onClick={() => { setShowAccountMenu(!showAccountMenu); setShowTeamsMenu(false) }}
                className="flex items-center gap-2 mono-label text-stone/75 hover:text-stone transition px-3 py-1.5 hover:bg-white/10 cursor-pointer"
              >
                <User size={15} />
                <span className="hidden sm:inline">{user.displayName || user.email}</span>
                <ChevronDown size={14} className="text-stone/70" />
              </button>
              {showAccountMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowAccountMenu(false)} />
                  {/* Grows with the number of teams, so it has to stay inside the
                      viewport. On a short window the lower items were unreachable. */}
                  <div className="absolute right-0 top-full mt-2 z-40 card card-shadow py-2 w-64 max-h-[calc(100vh-5rem)] overflow-y-auto">
                    {/* Account info */}
                    <div className="px-4 py-2 border-b border-navy/20">
                      <p className="text-sm font-semibold text-navy">{user.displayName || 'Account'}</p>
                      <p className="text-xs text-navy/70 truncate">{user.email}</p>
                    </div>

                    {/* Teams */}
                    {userOrgs.length > 0 && (
                      <div className="border-b border-navy/20 py-1">
                        <p className="px-4 py-1 mono-label text-teal">Teams</p>
                        {userOrgs.map(org => (
                          <button
                            key={org.id}
                            onClick={() => { navigate(`/org/${org.id}`); setShowAccountMenu(false) }}
                            className="w-full text-left px-4 py-2 text-sm text-navy hover:bg-paper transition flex items-center gap-2 cursor-pointer"
                          >
                            <Users size={14} className="text-navy/70" />
                            {org.name}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Create team */}
                    <button
                      onClick={() => { setShowCreateOrg(true); setShowAccountMenu(false) }}
                      className="w-full text-left px-4 py-2 text-sm text-amber font-semibold hover:bg-paper transition cursor-pointer"
                    >
                      + Create a team
                    </button>

                    {/* Sign out */}
                    <div className="border-t border-navy/20 pt-1">
                      <button
                        onClick={() => { signOut(); setShowAccountMenu(false) }}
                        className="w-full text-left px-4 py-2 text-sm text-navy/70 hover:text-navy hover:bg-paper transition flex items-center gap-2 cursor-pointer"
                      >
                        <LogOut size={14} />
                        Sign out
                      </button>
                      <button
                        onClick={() => { setShowDeleteAccount(true); setShowAccountMenu(false) }}
                        className="w-full text-left px-4 py-2 text-sm text-navy/70 hover:text-brick hover:bg-paper transition cursor-pointer"
                      >
                        Delete my data
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <button
            onClick={() => setModal('join')}
            className="mono-label border-2 border-stone/55 px-4 py-2 hover:bg-white/10 transition cursor-pointer"
          >
            Join
          </button>
          <button
            onClick={() => openStartModal('team')}
            className="mono-label border-2 border-stone/55 px-4 py-2 hover:bg-white/10 transition cursor-pointer"
          >
            Start
          </button>
          <button
            onClick={() => navigate('/solo')}
            className="mono-label bg-amber hover:bg-amber-dark text-white border-2 border-amber px-4 py-2 transition cursor-pointer"
          >
            Start solo
          </button>
        </div>
      </nav>

      {/* Sections */}
      <HeroSection
        onSolo={() => navigate('/solo')}
        onStart={() => openStartModal('team')}
        onScrollHow={scrollToHow}
      />
      <OldVsNewSection />
      <HowItWorksSection />
      <AgentShowcaseSection />
      <MethodologyTimeline />
      <BenefitsGrid />

      {/* Mid-page solo CTA */}
      <section className="bg-stone pb-14">
        <div className="mktg-container">
          <div className="max-w-3xl flex flex-col sm:flex-row sm:items-center gap-4 border-t-2 border-b border-navy py-5">
            <p className="descriptor text-navy/75 flex-1">
              About 25 minutes, free, and yours to keep.
            </p>
            <button onClick={() => navigate('/solo')} className="btn btn-amber shrink-0">
              Start a solo session
            </button>
          </div>
        </div>
      </section>

      <SecurityTrustSection />
      <WhatsIncludedSection onStart={() => openStartModal('team')} onSolo={() => navigate('/solo')} />
      <FaqSection />
      <FooterCta onStart={() => openStartModal('team')} onSolo={() => navigate('/solo')} />

      {/* Modal Overlay */}
      {modal && (
        <div
          className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={closeModal}
        >
          <div
            className="card card-shadow w-full max-w-md p-8 relative animate-modal-enter max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-navy/70 hover:text-navy transition cursor-pointer"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {modal === 'start' ? (
              <>
                <h2 className="hl text-2xl mb-6">Start a session</h2>
                {!authComplete ? (
                  <CreatorAuthGate onReady={handleAuthReady} />
                ) : (
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <label className="block mono-label text-navy/70 mb-1.5">Session Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { type: 'team' as const, label: 'Team Session', note: 'Up to 12 people' },
                          { type: 'solo' as const, label: 'Solo Session', note: 'Just you' },
                        ]).map(({ type, label, note }) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setSessionType(type)}
                            className={`border-2 px-3 py-2.5 text-sm text-left transition cursor-pointer ${
                              sessionType === type
                                ? 'border-navy bg-paper font-semibold shadow-[2px_2px_0_var(--color-navy)]'
                                : 'border-navy/55 hover:bg-paper'
                            }`}
                          >
                            {label}
                            <span className="block mono-label text-navy/70 font-normal mt-0.5">{note}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block mono-label text-navy/70 mb-1.5">Session Name</label>
                      <input
                        required
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Marketing team onboarding"
                        className="w-full border-2 border-navy/25 px-3.5 py-2.5 text-sm bg-paper focus:border-navy focus:outline-none"
                      />
                    </div>
                    {userOrgs.length > 0 && (
                      <div>
                        <label className="block mono-label text-navy/70 mb-1.5">Team (optional)</label>
                        <select
                          value={selectedOrgId}
                          onChange={(e) => setSelectedOrgId(e.target.value)}
                          className="w-full border-2 border-navy/25 px-3.5 py-2.5 text-sm bg-paper focus:border-navy focus:outline-none"
                        >
                          <option value="">No team</option>
                          {userOrgs.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {sessionType === 'team' && (
                    <div>
                      <label className="block mono-label text-navy/70 mb-1.5">Room Password</label>
                      <div className="relative">
                        <input
                          required
                          type={showStartPassword ? 'text' : 'password'}
                          value={startPassword}
                          onChange={(e) => setStartPassword(e.target.value)}
                          placeholder="Choose a password for your room"
                          className="w-full border-2 border-navy/25 px-3.5 py-2.5 pr-10 text-sm bg-paper focus:border-navy focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowStartPassword(!showStartPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/70 hover:text-navy transition cursor-pointer"
                          aria-label={showStartPassword ? 'Hide password' : 'Show password'}
                        >
                          {showStartPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    )}
                    <div>
                      <label className="block mono-label text-navy/70 mb-1.5">Your Display Name</label>
                      <input
                        required
                        value={startName}
                        onChange={(e) => setStartName(e.target.value)}
                        placeholder="Your name"
                        className="w-full border-2 border-navy/25 px-3.5 py-2.5 text-sm bg-paper focus:border-navy focus:outline-none"
                      />
                    </div>
                    {error && <p className="text-brick text-sm border-l-2 border-brick pl-3">{error}</p>}
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-amber w-full"
                    >
                      {loading ? 'Creating...' : 'Create session'}
                    </button>
                  </form>
                )}
              </>
            ) : (
              <>
                <h2 className="hl text-2xl mb-6">Join a session</h2>
                <form onSubmit={handleJoin} className="space-y-4">
                  <div>
                    <label className="block mono-label text-navy/70 mb-1.5">Room Code</label>
                    <input
                      required
                      maxLength={6}
                      value={roomCode}
                      onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                      placeholder="ABC123"
                      className="w-full border-2 border-navy/25 px-3.5 py-2.5 text-lg uppercase tracking-[0.35em] bg-paper font-mono focus:border-navy focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mono-label text-navy/70 mb-1.5">Room Password</label>
                    <div className="relative">
                      <input
                        required
                        type={showJoinPassword ? 'text' : 'password'}
                        value={joinPassword}
                        onChange={(e) => setJoinPassword(e.target.value)}
                        placeholder="Enter the room password"
                        className="w-full border-2 border-navy/25 px-3.5 py-2.5 pr-10 text-sm bg-paper focus:border-navy focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowJoinPassword(!showJoinPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/70 hover:text-navy transition cursor-pointer"
                        aria-label={showJoinPassword ? 'Hide password' : 'Show password'}
                      >
                        {showJoinPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block mono-label text-navy/70 mb-1.5">Your Display Name</label>
                    <input
                      required
                      value={joinName}
                      onChange={(e) => setJoinName(e.target.value)}
                      placeholder="Your name"
                      className="w-full border-2 border-navy/25 px-3.5 py-2.5 text-sm bg-paper focus:border-navy focus:outline-none"
                    />
                  </div>
                  {error && <p className="text-brick text-sm border-l-2 border-brick pl-3">{error}</p>}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-amber w-full"
                  >
                    {loading ? 'Joining...' : 'Join session'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {showDeleteAccount && (
        <DeleteAccountModal onClose={() => setShowDeleteAccount(false)} />
      )}

      {/* Create Org Modal */}
      {showCreateOrg && (
        <CreateOrgModal
          onClose={() => setShowCreateOrg(false)}
          onCreated={(orgId) => {
            setShowCreateOrg(false)
            navigate(`/org/${orgId}`)
          }}
        />
      )}
    </div>
  )
}
